// ====================================
// STYLES
// ====================================
/* Tailwindcss */
import './styles/tailwind.css';

/* Customizations */
import './styles/customizations/customizations.scss';

/* Swiper core layout (flex wrapper, hidden overflow). It ships with the main
   stylesheet, not with the lazy swiper-init chunk: until that chunk arrives the
   slides would stack vertically and the hero would collapse to half its height
   once Swiper loads, shifting the whole page (CLS). Kept after the project
   styles so the cascade matches the old load order, where it came last. */
import 'swiper/css';


// ====================================
// JS CORE (Alpine & HTMX)
// ====================================

// --- ALPINE.JS ---
import Alpine from 'alpinejs';
import intersect from '@alpinejs/intersect';
import persist from '@alpinejs/persist';

// --- PRODUCT FILTER URLs ---
// The product filter components are inline Alpine.data() factories in their
// templates, so they reach this through window rather than an import. It must
// be assigned before Alpine.start() below: start() fires alpine:init and
// evaluates x-data synchronously, and the factories call into it right there.
import {
  canonicalUrl,
  filtersFromLocation,
  filtersFromQuery,
  pageFromSearch,
  pageUrl,
} from '@/modules/product-filter-url';
(window as any).productFilterUrl = {
  canonicalUrl,
  filtersFromLocation,
  filtersFromQuery,
  pageFromSearch,
  pageUrl,
};

// Configura Alpine
Alpine.plugin(intersect);
Alpine.plugin(persist);
(window as any).Alpine = Alpine;
Alpine.store('ui', { sidebarOpen: false });
// Avvia Alpine
Alpine.start();

// --- HTMX ---
import htmx from 'htmx.org';
(window as any).htmx = htmx;
// ====================================
//️ CMS COMPOSER PLUGINS
// ====================================
import '../../../pleiadi_composer/static/pleiadi_composer/js/composer_plugins.js';

// ====================================
// DYNAMIC MODULE LOADER
// ====================================

/**
 * Core function that inspects the DOM and dynamically loads required modules.
 * Triggered on initial boot and after every HTMX content swap.
 */
async function initDynamicModules(root: HTMLElement = document.body) {

  // --- 1. PRELINE UI (Chunked Loading) ---
  // Accordion
  if (root.querySelector('[data-hs-accordion], .hs-accordion')) {
    import('@preline/accordion').then(({ default: HSAccordion }) => HSAccordion.autoInit());
  }
  // Dropdown
  if (root.querySelector('[data-hs-dropdown], .hs-dropdown-toggle')) {
    import('@preline/dropdown').then(({ default: HSDropdown }) => HSDropdown.autoInit());
  }
  // Collapse
  if (root.querySelector('[data-hs-collapse], .hs-collapse-toggle')) {
    import('@preline/collapse').then(({ default: HSCollapse }) => HSCollapse.autoInit());
  }
  // Overlay (Modals/Offcanvas)
  if (root.querySelector('[data-hs-overlay], .hs-overlay-toggle')) {
      console.info('IMPORTING PRELINE OVERLAY AND AUTOINIT');
    import('@preline/overlay').then(({ default: HSOverlay }) => HSOverlay.autoInit());
  }

  // --- 2. SWIPER (Heavy Library) ---
  if (root.querySelector('[data-swiper]')) {
    import('./modules/swiper-init').then(({ setupSwiperAutoInit }) => {
      setupSwiperAutoInit();
    });
  }

  // --- 2b. NETWORK CONTACTS CARDS: equalize the name-block height so the
  // addresses line up across cards. A grid subgrid can't reach the slider
  // layout (flex slides, no shared row tracks), so this is done in JS. ---
  if (root.querySelector('.network-contacts-list__card')) {
    import('@/modules/network-cards-equalize').then(({ initNetworkCardsEqualizer }) => {
      initNetworkCardsEqualizer(root);
    });
  }

  // --- 3. SITE MENUS ---
  if (root.querySelector('[data-menu="dropdown"]')) {
    import('../../../site_menus/static/site_menus/js/dropdown-menu.js').then(({ DropdownMenu }) => {
      root.querySelectorAll<HTMLElement>('[data-menu="dropdown"]').forEach(
        (el) => new DropdownMenu(el),
      );
    });
  }

  if (root.querySelector('[data-menu="drilldown"]')) {
    import('../../../site_menus/static/site_menus/js/drilldown-menu.js').then(({ DrilldownMenu }) => {
      // Idempotency guard: htmx:afterSettle fires with `detail.elt` (we
      // dispatch from openVariantModal) while the global listener falls back
      // to document.body when `detail.target` is missing, causing
      // initDynamicModules to walk the whole DOM. Without the guard we'd
      // build a second DrilldownMenu on the same <ul>, overwriting the
      // first one (and any pending attachPageToc({onBack}) it had installed).
      root.querySelectorAll<HTMLElement>('[data-menu="drilldown"]').forEach(
        (el) => {
          if (!(el as any).__drilldown) new DrilldownMenu(el);
        },
      );
    });
  }

  // --- 4. FANCYBOX (Heavy Library) ---
  if (root.querySelector('[data-fancybox]')) {
    import('@/modules/fancybox-init').then(({ initFancyBox }) => {
      initFancyBox();
    });
  }

  // --- 6. FORM CLIENT VALIDATION (contact, newsletter, …) ---
  // Presentation-only layer over the native Constraint Validation API; see the
  // anti-divergence contract in the module docstring. Any form[data-validate]
  // opts in — also runs on HTMX-swapped fragments (e.g. the newsletter modal).
  if (root.querySelector('form[data-validate]')) {
    import('@/modules/form-validate').then(({ initFormValidation }) => {
      initFormValidation(root);
    });
  }

  // --- 7. reCAPTCHA v3 token for HTMX-submitted forms (newsletter modal, …) ---
  // The native django_recaptcha widget calls form.submit() and bypasses HTMX;
  // this generates the token and feeds it into the HTMX request instead.
  if (root.querySelector('input[name="captcha"][data-recaptcha-sitekey]')) {
    import('@/modules/recaptcha-htmx').then(({ initRecaptchaHtmx }) => {
      initRecaptchaHtmx(root);
    });
  }

  // --- 5. GALLERY IMAGE ERROR GUARD (immagini gallery non disponibili / 404 CDN) ---
  // When a gallery image fails to load (e.g. THRON CDN 404), hide its slide and
  // refresh Swiper so no empty/broken slot is shown. Ambience slides have no
  // intrinsic width (width follows the image), so a placeholder makes no sense
  // here — dropping the slide is the intended behaviour.
  if (root.querySelector('[data-gallery-img]')) {
    import('@/modules/gallery-image-error-handler').then(({ initGalleryImageErrorGuard }) => {
      initGalleryImageErrorGuard(root);
    });
  }

  // --- 6. BROKEN IMAGE FALLBACK (404 CDN / immagine assente) ---
  // When an <img data-img-fallback> fails to load, hide it so the neutral grey
  // placeholder sitting underneath shows through (instead of the broken-image
  // icon + alt text). Reacts to the browser `error` event; no THRON call.
  // Loaded unconditionally on purpose: gating on `querySelector` missed the
  // images Alpine renders after the initial fetch (collection list), which
  // never re-run this hook. The guard is a single passive capture listener,
  // so installing it on every page costs nothing.
  import('@/modules/broken-image-fallback').then(({ initBrokenImageFallback }) => {
    initBrokenImageFallback(root);
  });

  // --- 4. RE-INIT ALPINE TREE (If necessary for HTMX) ---
  // If the root is not the body, it implies it is an HTMX fragment.
  // Alpine.initTree is used to initialize newly injected Alpine components.
  if (root !== document.body && (window as any).Alpine) {
     (window as any).Alpine.initTree(root);
  }
}


// ====================================
// EVENTS HANDLERS
// ====================================

// 1. Initial Load
document.addEventListener('DOMContentLoaded', () => {
  initDynamicModules(document.body);
});

// 2. HTMX After Settle (Navigation/Swap)
document.body.addEventListener('htmx:afterSettle', (e: any) => {
  const target = (e?.detail?.target as HTMLElement) || document.body;
  // Usiamo requestAnimationFrame per assicurarci che il DOM sia pronto
  requestAnimationFrame(() => initDynamicModules(target));
});

